import React from "react";
import { AppMarkdown } from "../../../../shared/AppMarkdown/AppMarkdown";
import StandardButton from "../../../../shared/formComponents/StandardButton/StandardButton";
import { day } from "../../_config/okrPlansCoachFormat";
import "../../_shared/OpcDoc/_opc-doc.scss";

// ═══════════════════════════════════════════════════════════════════════════
// The final-review panel (BE 2.9; CD20, CD47) — today's ProjectSummary markup,
// laid out as the final report (BE FE67 / D265; same as the SME summary):
//   header (objective, review date, submitted by … on …, Finalise review /
//   Finalised …) → Your Remarks → Executive summary → what the owner
//   submitted (CD56): Outcome (`outcome_label`), What went well, What got in
//   the way, For you → Key results (statement + final %) → Initiatives.
// No objective status, no "Final assessment", no "At the lock", no per-item
// notes (FE65, FE67). Lists come from `summary_final` (else the AI draft,
// else what the owner submitted): `key_results[] / initiatives[]` with
// `id`, `statement`, `achieved_pct`.
// ═══════════════════════════════════════════════════════════════════════════

const pctOf = (k) => k.achieved_pct ?? k.progress ?? k.value ?? 0;

// CD51 / CD53: the SME review summary's list — heading + count, grey rows
// with the number badge (grey for initiatives), statement and %.
const Items = ({ title, items, plain }) =>
  items?.length ? (
    <section className="opc-doc__section">
      <div className="opc-doc__head">
        <h3 className="opc-doc__h">{title}</h3>
        <span className="opc-doc__count">{items.length}</span>
      </div>
      <ul className="opc-doc__rows">
        {items.map((k, i) => (
          <li key={k.id || k.key_result_id || k.initiative_id || i}>
            <span className="opc-doc__item">
              <span className={`opc-num${plain ? " is-plain" : ""}`}>
                {i + 1}
              </span>
              <span>{k.statement || k.label}</span>
            </span>
            <strong>{pctOf(k)}%</strong>
          </li>
        ))}
      </ul>
    </section>
  ) : null;

const Tile = ({ label, children }) =>
  children ? (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  ) : null;

const ReviewPanel = ({
  plan,
  review,
  owner,
  ownerFull,
  finalising,
  onFinalise,
}) => {
  const finalised = review.state === "REVIEWED" || !!review.finalised_at;
  const submitted = review.submitted;
  const s = review.summary_final || review.summary_draft || {};
  const remark = review.coach_remark;
  const values = submitted?.values || {};
  const krs = s.key_results?.length ? s.key_results : values.key_results || [];
  const inis = s.initiatives?.length ? s.initiatives : values.initiatives || [];
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between project-summary-header p-3">
        <div className="d-flex flex-column">
          <span className="dark-grey fw-bold">{plan.objective?.text}</span>
          <div className="d-flex align-items-center">
            <span className="light-text small">
              Review Date:{" "}
              <span className="fw-bold dark-grey">{day(plan.review_date)}</span>
            </span>
            {submitted ? (
              <>
                <div className="vertical-seprator mx-3" />
                <span className="light-text small">
                  Submitted by {ownerFull} on{" "}
                  <span className="fw-bold dark-grey">
                    {day(submitted.submitted_at || review.submitted_at)}
                  </span>
                </span>
              </>
            ) : null}
          </div>
        </div>
        {finalised ? (
          <div className="d-flex align-items-center">
            <img
              src="/assets/images/true-primary.svg"
              alt=""
              className="me-2"
              height={16}
              width={16}
            />
            <span className="small dark-grey fw-bold">
              Finalised {day(review.finalised_at)}
            </span>
          </div>
        ) : submitted && remark?.text ? (
          // CD45: Finalise only once the coach's remark is saved.
          <StandardButton
            text={finalising ? "Finalising…" : "Finalise review"}
            color="btn-rounded-brand-blue-new"
            className="btn-xs opc-finalise-btn"
            disabled={finalising}
            onClick={onFinalise}
          />
        ) : null}
      </div>
      <div className="summary-details">
        <div className="coach-review mt-3 pb-2 px-3">
          <div className="d-flex align-items-center">
            <img src="/assets/images/user-review.svg" alt="" />
            <span className="fw-bold dark-grey small ms-2">Your Remarks</span>
          </div>
          {remark?.text ? (
            <div className="coach-remarks-main p-3 mt-3 small">
              <AppMarkdown>{remark.text}</AppMarkdown>
            </div>
          ) : (
            <div className="coach-remarks-dashed mt-3 p-3">
              <span className="small">
                Draft your remark by chatting with the assistant, then save it
                from the card. You can finalise the review once it&apos;s saved;{" "}
                {owner} sees the summary only after that.
              </span>
            </div>
          )}
        </div>

        {/* CD53: the rest is the SME review summary's layout. */}
        <section className="opc-doc__section">
          <h3 className="opc-doc__h">Executive Summary</h3>
          <p className="opc-doc__summary">{s.executive_summary || "—"}</p>
        </section>
        <section className="opc-doc__section">
          <h3 className="opc-doc__h">What {owner} submitted</h3>
          {submitted ? (
            <dl className="opc-doc__tiles">
              <Tile label="Outcome">{submitted.outcome_label}</Tile>
              <Tile label="What went well">{submitted.went_well || "—"}</Tile>
              <Tile label="What got in the way">
                {submitted.got_in_the_way || "—"}
              </Tile>
              <Tile label="For you">{submitted.for_coach}</Tile>
            </dl>
          ) : (
            <p className="opc-doc__note">
              {owner} hasn&apos;t submitted the review yet. You can finalise it
              once they do.
            </p>
          )}
        </section>
        <Items title="Key Results" items={krs} />
        <Items title="Initiatives" items={inis} plain />
      </div>
    </div>
  );
};

export default ReviewPanel;
